import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { fmtNumber, useT } from '@/i18n'
import { inventoryMessages } from '@/i18n/messages/inventory'
import { Dialog, Field, GhostButton, INPUT, PrimaryButton } from './shared'
import type { Location, ProjectChoice } from './types'

/** The warehouse and site stores; site stores list the projects they serve. */
export function LocationsDialog({ canEdit, onClose }: { canEdit: boolean; onClose: () => void }) {
  const t = useT(inventoryMessages)
  const { data } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: () => api.get<Location[]>('/api/inventory/locations') })
  const [editing, setEditing] = useState<Location | 'new' | null>(null)

  if (editing) return <LocationForm location={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
  return (
    <Dialog title={t('locationsTitle')} hint={t('locationsHint')} onClose={onClose}
      footer={canEdit ? <PrimaryButton onClick={() => setEditing('new')}>+ {t('addLocation')}</PrimaryButton> : undefined}>
      <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {(data ?? []).map((l) => (
          <li key={l.id} className={`flex items-center justify-between gap-3 py-2.5 ${l.active ? '' : 'opacity-50'}`}>
            <div className="min-w-0">
              <div className="font-medium">{l.name}</div>
              <div className="text-xs text-neutral-500">
                {l.kind === 'central' ? t('kindCentral') : t('kindSite')}
                {l.bauleiterName && ` · ${l.bauleiterName}`}
                {' · '}{t('drumsAt', { count: l.drums })} · {fmtNumber(l.remainingM)} m
                {!l.active && ` · ${t('inactive')}`}
              </div>
            </div>
            {canEdit && <GhostButton onClick={() => setEditing(l)}>{t('edit')}</GhostButton>}
          </li>
        ))}
      </ul>
    </Dialog>
  )
}

function LocationForm({ location, onClose }: { location: Location | null; onClose: () => void }) {
  const t = useT(inventoryMessages)
  const qc = useQueryClient()
  const projects = useQuery<ProjectChoice[]>({ queryKey: ['inv-projects'], queryFn: () => api.get<ProjectChoice[]>('/api/inventory/projects'), staleTime: 10 * 60_000 })
  const [kind, setKind] = useState<'central' | 'site'>(location?.kind ?? 'site')
  const [name, setName] = useState(location?.name ?? '')
  const [bauleiter, setBauleiter] = useState(location?.bauleiterName ?? '')
  const [active, setActive] = useState(location?.active ?? true)
  const [picked, setPicked] = useState<Set<string>>(new Set(location?.projectIds ?? []))
  const [filter, setFilter] = useState('')
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => {
      const body = { kind, name: name.trim(), bauleiterName: bauleiter.trim() || null, active, projectIds: [...picked] }
      return location ? api.put(`/api/inventory/locations/${location.id}`, body) : api.post('/api/inventory/locations', body)
    },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['inv-locations'] }); onClose() },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  })
  const f = filter.trim().toLowerCase()
  const list = (projects.data ?? []).filter((p) => picked.has(p.projectId) || !f
    || (p.name ?? '').toLowerCase().includes(f) || (p.codeInternal ?? '').includes(f))

  return (
    <Dialog title={location ? location.name : t('addLocation')} onClose={onClose}
      footer={<><GhostButton onClick={onClose}>{t('cancel')}</GhostButton>
        <PrimaryButton disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>{t('save')}</PrimaryButton></>}>
      {!location && (
        <div className="grid grid-cols-2 gap-2">
          {(['site', 'central'] as const).map((k) => (
            <button key={k} type="button" onClick={() => setKind(k)}
              className={`min-h-11 rounded-xl text-sm font-medium ring-1 ${kind === k ? 'bg-brand-600 text-white ring-brand-600' : 'ring-neutral-300 dark:ring-neutral-700'}`}>
              {k === 'site' ? t('kindSite') : t('kindCentral')}
            </button>
          ))}
        </div>
      )}
      <Field label={t('name')}><input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className={INPUT} /></Field>
      {kind === 'site' && (
        <>
          <Field label={t('bauleiter')}><input value={bauleiter} onChange={(e) => setBauleiter(e.target.value)} maxLength={200} className={INPUT} /></Field>
          <Field label={t('projects')}>
            <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} className={INPUT} />
            <div className="max-h-52 space-y-0.5 overflow-y-auto rounded-lg ring-1 ring-neutral-200 p-1 dark:ring-neutral-800">
              {list.map((p) => (
                <label key={p.projectId} className="flex min-h-10 items-center gap-2 rounded-md px-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800">
                  <input type="checkbox" checked={picked.has(p.projectId)} onChange={() => setPicked((s) => {
                    const n = new Set(s); if (n.has(p.projectId)) n.delete(p.projectId); else n.add(p.projectId); return n
                  })} />
                  <span className="min-w-0 truncate">{p.name ?? '—'}{p.codeInternal && <span className="text-neutral-400"> · {p.codeInternal}</span>}</span>
                </label>
              ))}
            </div>
          </Field>
        </>
      )}
      {location && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> {t('active')}
        </label>
      )}
      {error && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('couldNotSave', { error })}</p>}
    </Dialog>
  )
}
